/**
 * SubscriptionContext - Consolidated Subscription & Payment State
 *
 * Purpose: Single source of truth for ALL subscription/payment-related state
 *
 * PERF FIX: This context consolidates 4 separate real-time subscriptions into 1:
 * - useSubscriptionAccess (subscriptions table)
 * - useUserTier (subscriptions + payments tables)
 * - useBillingAccess (consultant_usage_tracking table)
 *
 * Benefits:
 * - 4 subscriptions → 1 subscription (75% reduction)
 * - Single fetch on auth, cached for all consumers
 * - No duplicate queries to same tables
 * - Memoized context value prevents cascading re-renders
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/utils/logger';

// Types
export type UserTier = 'discover' | 'single' | 'pro_20' | 'pro_40' | 'pro_100' | 'enterprise' | null;

interface SubscriptionState {
  // Tier info (from useUserTier)
  tier: UserTier;
  hasFullAccess: boolean;
  creditsRemaining: number | null;
  subscriptionExpires: Date | null;

  // Subscription access (from useSubscriptionAccess)
  hasAccess: boolean;
  isPaused: boolean;
  status: string | null;
  currentPeriodEnd: string | null;
  pausedAt: string | null;
  paymentFailed: boolean;
  paymentFailedAt: string | null;
  gracePeriodDaysRemaining: number;
  gracePeriodExpired: boolean;

  // Billing access (from useBillingAccess)
  billingBlocked: boolean;
  unpaidInvoice: {
    billing_month: string;
    amount: number;
    status: string;
    stripe_invoice_id: string | null;
    error: string | null;
  } | null;
  billingDaysOverdue: number;
  requiresPayment: boolean;

  // Loading state
  isLoading: boolean;

  // Actions
  refetch: () => Promise<void>;
}

const defaultState: SubscriptionState = {
  tier: null,
  hasFullAccess: false,
  creditsRemaining: null,
  subscriptionExpires: null,
  hasAccess: true,
  isPaused: false,
  status: null,
  currentPeriodEnd: null,
  pausedAt: null,
  paymentFailed: false,
  paymentFailedAt: null,
  gracePeriodDaysRemaining: 0,
  gracePeriodExpired: false,
  billingBlocked: false,
  unpaidInvoice: null,
  billingDaysOverdue: 0,
  requiresPayment: false,
  isLoading: true,
  refetch: async () => {},
};

const SubscriptionContext = createContext<SubscriptionState>(defaultState);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

// Cache key for localStorage
const getCacheKey = (userId: string) => `subscription_state_${userId}`;

interface SubscriptionProviderProps {
  children: React.ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [state, setState] = useState<Omit<SubscriptionState, 'refetch'>>(() => {
    // Try to load from cache for instant display
    if (user?.id) {
      try {
        const cached = localStorage.getItem(getCacheKey(user.id));
        if (cached) {
          const parsed = JSON.parse(cached);
          const cacheAge = Date.now() - parsed.timestamp;
          if (cacheAge < 5 * 60 * 1000) { // 5 minutes
            return { ...parsed.data, isLoading: false };
          }
        }
      } catch (e) {
        logger.error('Failed to parse cached subscription state:', e);
      }
    }
    return { ...defaultState, isLoading: true };
  });

  // Consolidated fetch function
  const fetchAll = useCallback(async () => {
    if (!user) {
      setState({
        ...defaultState,
        isLoading: false,
      });
      return;
    }

    try {
      // PERF DIAGNOSTIC: Track subscription context API calls
      const startTime = Date.now();
      console.log(`💳 [PERF] SubscriptionContext fetchAll started at ${new Date().toISOString().split('T')[1]}`);

      // Fetch all data in parallel
      const [subscriptionResult, tierResult, billingResult] = await Promise.all([
        // 1. Subscription status
        supabase
          .from('subscriptions')
          .select('status, current_period_end, paused_at, payment_failed_at, grace_period_days')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),

        // 2. User tier/payment status
        supabase
          .from('user_payment_status')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle(),

        // 3. Billing access
        supabase.rpc('check_billing_access', { p_user_id: user.id }),
      ]);

      // PERF DIAGNOSTIC: Log API call completion
      console.log(`💳 [PERF] SubscriptionContext fetchAll completed in ${Date.now() - startTime}ms`);

      // Process subscription data
      let hasAccess = true;
      let isPaused = false;
      let status: string | null = null;
      let currentPeriodEnd: string | null = null;
      let pausedAt: string | null = null;
      let paymentFailed = false;
      let paymentFailedAt: string | null = null;
      let gracePeriodDaysRemaining = 0;
      let gracePeriodExpired = false;

      const subscription = subscriptionResult.data;
      if (subscription) {
        status = subscription.status;
        currentPeriodEnd = subscription.current_period_end;
        pausedAt = subscription.paused_at;

        // Check for payment failure with grace period
        if (subscription.status === 'past_due' && subscription.payment_failed_at) {
          paymentFailed = true;
          paymentFailedAt = subscription.payment_failed_at;

          const now = new Date();
          const failedAt = new Date(subscription.payment_failed_at);
          const gracePeriodDays = subscription.grace_period_days || 15;
          const gracePeriodEnd = new Date(failedAt.getTime() + gracePeriodDays * 24 * 60 * 60 * 1000);
          const msRemaining = gracePeriodEnd.getTime() - now.getTime();
          const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));

          gracePeriodExpired = now >= gracePeriodEnd;
          gracePeriodDaysRemaining = Math.max(0, daysRemaining);
          hasAccess = !gracePeriodExpired;
        }

        // Check if subscription is paused
        if (subscription.status === 'paused') {
          isPaused = true;
          const now = new Date();
          const periodEnd = new Date(subscription.current_period_end);
          hasAccess = now <= periodEnd;
        } else if (subscription.status === 'active') {
          hasAccess = true;
        } else if (['canceled', 'incomplete', 'incomplete_expired', 'unpaid'].includes(subscription.status)) {
          hasAccess = false;
        }
      }

      // Process tier data
      let tier: UserTier = null;
      let hasFullAccess = false;
      let creditsRemaining: number | null = null;
      let subscriptionExpires: Date | null = null;

      const tierData = tierResult.data;
      if (tierData?.subscription_status === 'active') {
        const productId = tierData.subscription_product_id;
        if (productId?.includes('single') || productId?.includes('solo')) {
          tier = 'single';
          hasFullAccess = true;
        } else if (productId?.includes('pro_20') || productId?.includes('pro-20')) {
          tier = 'pro_20';
          hasFullAccess = true;
        } else if (productId?.includes('pro_40') || productId?.includes('pro-40')) {
          tier = 'pro_40';
          hasFullAccess = true;
        } else if (productId?.includes('pro_100') || productId?.includes('pro-100')) {
          tier = 'pro_100';
          hasFullAccess = true;
        } else if (productId?.includes('enterprise')) {
          tier = 'enterprise';
          hasFullAccess = true;
        }
      } else if (tierData?.credits_remaining > 0) {
        tier = 'discover';
        hasFullAccess = false;
      }
      creditsRemaining = tierData?.credits_remaining || null;
      subscriptionExpires = tierData?.subscription_expires ? new Date(tierData.subscription_expires) : null;

      // Process billing data
      let billingBlocked = false;
      let unpaidInvoice = null;
      let billingDaysOverdue = 0;
      let requiresPayment = false;

      const billingData = billingResult.data;
      if (billingData) {
        billingBlocked = billingData.blocked || false;
        billingDaysOverdue = billingData.days_overdue || 0;
        requiresPayment = billingData.requires_payment || false;
        if (billingData.unpaid_invoice) {
          unpaidInvoice = {
            billing_month: billingData.unpaid_invoice.billing_month,
            amount: billingData.unpaid_invoice.amount,
            status: billingData.unpaid_invoice.status,
            stripe_invoice_id: billingData.unpaid_invoice.stripe_invoice_id,
            error: billingData.unpaid_invoice.error,
          };
        }
      }

      const newState = {
        tier,
        hasFullAccess,
        creditsRemaining,
        subscriptionExpires,
        hasAccess,
        isPaused,
        status,
        currentPeriodEnd,
        pausedAt,
        paymentFailed,
        paymentFailedAt,
        gracePeriodDaysRemaining,
        gracePeriodExpired,
        billingBlocked,
        unpaidInvoice,
        billingDaysOverdue,
        requiresPayment,
        isLoading: false,
      };

      // Cache for 5 minutes
      try {
        localStorage.setItem(getCacheKey(user.id), JSON.stringify({
          data: newState,
          timestamp: Date.now(),
        }));
      } catch (e) {
        logger.error('Failed to cache subscription state:', e);
      }

      setState(newState);
    } catch (error) {
      logger.error('Error fetching subscription state:', error);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [user]);

  // Fetch on mount and user change
  useEffect(() => {
    fetchAll();

    // Single consolidated real-time subscription
    if (!user?.id) return;

    const channel = supabase
      .channel('consolidated-subscription-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          logger.debug('Subscription changed, refetching...');
          fetchAll();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payments',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          logger.debug('Payment changed, refetching...');
          fetchAll();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'consultant_usage_tracking',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          logger.debug('Billing changed, refetching...');
          fetchAll();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, fetchAll]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo<SubscriptionState>(() => ({
    ...state,
    refetch: fetchAll,
  }), [state, fetchAll]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

// Compatibility hooks - these use the consolidated context
// but maintain the same API as the original hooks

/**
 * useUserTier - Compatibility hook for tier access
 * Uses the consolidated SubscriptionContext internally
 */
export function useUserTierFromContext() {
  const { tier, hasFullAccess, creditsRemaining, subscriptionExpires, isLoading } = useSubscription();
  return {
    tier,
    isLoading,
    hasFullAccess,
    creditsRemaining,
    subscriptionExpires,
  };
}

/**
 * useSubscriptionAccess - Compatibility hook for subscription access
 * Uses the consolidated SubscriptionContext internally
 */
export function useSubscriptionAccessFromContext() {
  const {
    hasAccess,
    isPaused,
    isLoading,
    currentPeriodEnd,
    pausedAt,
    status,
    paymentFailed,
    paymentFailedAt,
    gracePeriodDaysRemaining,
    gracePeriodExpired,
    refetch,
  } = useSubscription();

  return {
    hasAccess,
    isPaused,
    isLoading,
    currentPeriodEnd,
    pausedAt,
    status,
    paymentFailed,
    paymentFailedAt,
    gracePeriodDaysRemaining,
    gracePeriodExpired,
    refetch,
  };
}

/**
 * useBillingAccess - Compatibility hook for billing access
 * Uses the consolidated SubscriptionContext internally
 */
export function useBillingAccessFromContext() {
  const {
    billingBlocked,
    unpaidInvoice,
    billingDaysOverdue,
    requiresPayment,
    isLoading,
    refetch,
  } = useSubscription();

  return {
    hasAccess: !billingBlocked,
    isBlocked: billingBlocked,
    isLoading,
    unpaidInvoice,
    daysOverdue: billingDaysOverdue,
    requiresPayment,
    refetch,
  };
}
